import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '../slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithLogout = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    window.location.href = '/login';
  }

  return result;
};

// Build a querystring from a params object, dropping empty values.
const qs = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.append(k, v);
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};

// Workstream F — admin operations slice. Mirrors analyticsApi: all endpoints
// unwrap the { success, data } envelope to `data`. Reports are read-views;
// address book + rules carry mutations with cache invalidation.
export const adminOpsApi = createApi({
  reducerPath: 'adminOpsApi',
  baseQuery: baseQueryWithLogout,
  tagTypes: ['AddressBook', 'Rules'],
  endpoints: (builder) => ({
    // §8 Solvency/liability: Σ merchant liabilities vs on-chain holdings per chain.
    getSolvency: builder.query({
      query: () => '/admin/reports/solvency',
      transformResponse: (response) => response?.data || null,
    }),

    // §8 Treasury: hot/cold balances + gas-tank levels per chain.
    getTreasury: builder.query({
      query: () => '/admin/reports/treasury',
      transformResponse: (response) => response?.data || null,
    }),

    // §8 Merchant dossier (ongoing report).
    getMerchantDossier: builder.query({
      query: (id) => `/admin/merchants/${id}/dossier`,
      transformResponse: (response) => response?.data || null,
    }),

    // §8 Per-merchant reconciliation (ledger vs on-chain).
    getMerchantReconciliation: builder.query({
      query: (id) => `/admin/merchants/${id}/reconciliation`,
      transformResponse: (response) => response?.data || null,
    }),

    // §6 Five log surfaces with filters. surface: ip|merchant|customer|admin|security.
    getLogs: builder.query({
      query: ({ surface, ...filters }) => `/admin/logs/${surface}${qs(filters)}`,
      transformResponse: (response) => response?.data || null,
    }),

    // §6 Address book — per-merchant whitelist management.
    getAddresses: builder.query({
      query: (id) => `/admin/merchants/${id}/addresses`,
      transformResponse: (response) => response?.data || [],
      providesTags: ['AddressBook'],
    }),
    addAddress: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/merchants/${id}/addresses`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AddressBook'],
    }),
    deleteAddress: builder.mutation({
      query: ({ id, addressId }) => ({
        url: `/admin/merchants/${id}/addresses/${addressId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AddressBook'],
    }),

    // §6 Rules engine — view / edit §3 thresholds.
    getRules: builder.query({
      query: () => '/admin/rules',
      transformResponse: (response) => response?.data || null,
      providesTags: ['Rules'],
    }),
    updateRules: builder.mutation({
      query: (body) => ({
        url: '/admin/rules',
        method: 'PUT',
        body,
      }),
      transformResponse: (response) => response?.data || null,
      invalidatesTags: ['Rules'],
    }),
  }),
});

export const {
  useGetSolvencyQuery,
  useGetTreasuryQuery,
  useGetMerchantDossierQuery,
  useGetMerchantReconciliationQuery,
  useGetLogsQuery,
  useGetAddressesQuery,
  useAddAddressMutation,
  useDeleteAddressMutation,
  useGetRulesQuery,
  useUpdateRulesMutation,
} = adminOpsApi;

// Log export is a direct download (watermarked CSV/JSON) — outside RTK Query.
// Helper builds the authed URL the page fetches as a blob.
export const buildLogExportUrl = (surface, filters = {}, format = 'csv') => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return `${base}/admin/logs/${surface}/export${qs({ ...filters, format })}`;
};
